<template>
  <RouterLink
    v-if="quote"
    :to="linkOnPost(getPost.id)"
    class="quote post relative text-foregroundPrimary cursor-pointer my-3">
    <div v-if="getPost?.authorUser" class="grow" ref="quoteContainer">
      <div class="post__body">
        <div class="post__header items-center mb-2">
          <div class="quote__avatar-container pr-1">
            <BaseAvatar
              v-if="!getPost?.channel?.organization"
              :storage-id="getPost?.authorUser?.avatarFile?.storageId"
              size="quote__avatar-size" />
            <BaseAvatar
              v-if="getPost?.channel?.organization?.id && !getPost?.displayedChannel"
              :storage-id="getPost?.channel?.organization?.avatarFile?.storageId"
              size="quote__avatar-size" />
            <BaseAvatar
              v-if="getPost?.channel?.organization?.id && getPost?.displayedChannel"
              :storage-id="getPost?.displayedChannel.user?.avatarFile?.storageId"
              size="quote__avatar-size" />
          </div>
          <div class="flex flex-wrap items-center gap-x-2">
            <span
              v-if="
                (getPost?.authorUser?.firstName || getPost?.authorUser?.lastName) && !getPost?.channel?.organization
              "
              class="post__username mr-3"
              >{{ getPost?.authorUser?.firstName }} {{ getPost?.authorUser?.lastName }}
              <span class="font-light ml-2 text-[0.8rem]">@{{ getPost?.authorUser?.username }}</span></span
            >
            <span v-if="getPost?.channel?.organization && !getPost?.displayedChannel" class="post__username mr-3">{{
              getPost?.channel?.organization.name
            }}</span>

            <span v-if="getPost?.channel?.organization && getPost?.displayedChannel" class="post__username mr-3"
              >{{ getPost?.displayedChannel?.user?.firstName }} {{ getPost?.displayedChannel?.user?.lastName }}
              <span class="font-light ml-2 text-[0.8rem]">@{{ getPost?.displayedChannel?.user?.username }}</span></span
            >
            <span
              v-if="getPost?.channel?.organization && !getPost?.displayedChannel"
              class="post__badge post__badge--grey"
              >@{{ getPost?.channel?.organization.name }}</span
            >
          </div>
          <span class="post__date">{{ formattedTime }}</span>
        </div>
        <div class="post__content">
          <div class="post-container">
            <div class="post-content">
              <h2 class="post__title text-foregroundPrimary" v-if="getPost.title">
                {{ getPost.title }}
              </h2>
              <p id="text" class="line-clamp-3" v-html="getPost?.text"></p>
            </div>
          </div>

          <div
            v-if="getPost.attachedFiles?.length"
            @click="blockLink"
            class="grid gap-3 mt-3"
            :class="imageGrids[getPost.attachedFiles.length].wrapper">
            <ImageWithPreview
              v-for="(postFile, index) in getPost?.attachedFiles"
              :key="postFile.id"
              :src="BaseURL + postFile.storageId"
              :baseurl="BaseURL"
              :post="postFile"
              :classes="getPost?.attachedFiles.length === 1 && isMainPost ? 'full-height-image' : ''" />
          </div>
          <div class="text-sm text-foregroundPrimary" v-if="quoteOriginalLink">{{ quoteOriginalLink }}</div>
        </div>
      </div>
    </div>
  </RouterLink>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { BaseURL, linkUrl } from '@/global/baseUrls.js';
import { getRelativeTime } from '@/utils/relativeTime';
import { imageGrids } from '../../utils/styles/imageGrids.js';
import ImageWithPreview from '@/components/ImageWithPreview/ImageWithPreview.vue';

const store = useStore();
const route = useRoute();
const checkReply = route.meta.isReply;

const props = defineProps({
  quote: {
    quote: Object,
  },
});

const getPost = computed(() => {
  return props.quote;
});

const linkOnPost = id => {
  return { name: checkReply ? 'reply' : 'post', params: { id } };
};

const quoteOriginalLink = computed(() => {
  if (getPost?.value.type === 'quote' && getPost.value.repostedPostId) {
    const quoteId = getPost.value.repostedPostId;
    const link = `${linkUrl}/post/${quoteId}`;
    const maxLength = 40;
    return link.length > maxLength ? link.slice(0, maxLength) + '…' : link;
  } else return '';
});

const fullName = computed(() => {
  const user = getPost.value.authorUser;

  if (!user) {
    return '';
  }

  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const userName = user.username;

  if (!firstName && !lastName) {
    return userName || '';
  }

  return `${firstName} ${lastName}`.trim();
});

const now = computed(() => store.getters.getCurrentTime);
const quoteContainer = ref(null);
const postWidth = ref(0);
const updatePostWidth = () => {
  if (quoteContainer.value) {
    postWidth.value = quoteContainer.value.offsetWidth;
  }
};
const isCompactDate = computed(() => postWidth.value < 400);
const formattedTime = computed(() => {
  return getRelativeTime(getPost.value.publishedAt, now, isCompactDate.value);
});

onMounted(() => {
  nextTick(() => {
    updatePostWidth();
  });
  window.addEventListener('resize', updatePostWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', updatePostWidth);
});
</script>

<style lang="scss" scoped>
.post-container {
  cursor: pointer;
}
.post-content {
  height: auto;
}
.post__title {
  font-size: 16px;
}
#text {
  font-size: 14px;
}
.flex {
  display: flex;
}
.quote.post {
  border: 1px solid var(--ui-04);
  border-radius: 12px;
}
.quote__avatar-size {
  width: 28px;
  height: 28px;
}
.quote__avatar-container {
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 12px;
  z-index: 2;
}
</style>
